import { computed, inject, Injectable, signal } from '@angular/core';
import { BuilderService } from './builder.service';
import { ClipboardService } from '../../pages/new-builder-dashboard/clipboard.service';
import {
  isSubSectionStructureObject,
  SectionStructureObject,
} from '../../api/interfaces';

@Injectable()
export class SectionService {
  private readonly builderService = inject(BuilderService);
  protected clipboardService = inject(ClipboardService);
  private readonly _uniqueId = signal<string | undefined>(undefined);

  get uniqueId(): string | undefined {
    return this._uniqueId();
  }

  set uniqueId(uniqueId: string) {
    this._uniqueId.set(uniqueId);
  }

  section = computed(() => {
    const id = this._uniqueId();
    if (!id) return undefined;
    return this.builderService.moduleStructure()[id] as SectionStructureObject;
  });

  sectionPasteEnabled = computed(() => {
    return this.clipboardService.isSectionStructure();
  });

  subsectionPasteEnabled = computed(() => {
    return this.clipboardService.isSubSectionStructure();
  });

  sectionCustomizationVisible = signal(false);

  subsectionPointsOverMaxAllowed = computed(() => {
    const moduleStructure = this.builderService.moduleStructure();
    const section = this.section();
    if (!section) return false;

    const maxPoints = section.customization.points || 0;
    const totalSubsectionPoints = section.children.reduce(
      (sum, subsectionId) => {
        const subsection = moduleStructure[subsectionId];
        if (!subsection || !isSubSectionStructureObject(subsection)) return sum;
        return sum + (subsection.customization.points || 0);
      },
      0,
    );
    return totalSubsectionPoints > maxPoints;
  });

  removeSection() {
    const uniqueId = this._uniqueId();
    if (!uniqueId) return;

    this.builderService.removeSection(uniqueId);
  }

  addSubSection() {
    const sectionId = this._uniqueId();
    if (!sectionId) return;

    this.builderService.addSubsection(sectionId);
  }

  removeSubSection(subSectionId: string) {
    const sectionId = this._uniqueId();
    if (!sectionId) return;
    this.builderService.removeSubsection(sectionId, subSectionId);
  }

  copySection() {
    const section = this.section();
    if (!section) return;
    this.clipboardService.copy(section);
  }

  pasteSection() {
    this.clipboardService.paste('root');
  }

  pasteSubSection() {
    const sectionId = this._uniqueId();
    if (!sectionId) return;
    this.clipboardService.paste(sectionId);
  }

  moveSubSection(
    previousContainerId: string,
    subSectionId: string,
    index: number,
  ) {
    const sectionId = this._uniqueId();
    if (!sectionId) return;

    this.builderService.moveSubsection(
      sectionId,
      previousContainerId,
      subSectionId,
      index,
    );
  }

  updateSection(
    newSectionId: string,
    customization: {
      title: string;
      subTitle: string;
      description: string;
      points: number;
    },
  ) {
    const oldSectionId = this._uniqueId();
    if (!oldSectionId) return;

    this.builderService.updateSection(
      oldSectionId,
      newSectionId,
      customization,
    );
  }
}
